import { useCallback, useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { isBefore, isSameDay, startOfDay } from 'date-fns';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Form } from '@/components/ui/form';
import { DateRangePicker } from '@/modules/common';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { STANDARD_DATE_FORMAT_DAYJS } from '@/utils/date-formatters';

import { FeatureFooterProps, FeatureMode } from '../types/types';

const dateRangeSchema = z.object({
  dateRange: z.object({
    from: z.date({ required_error: 'common:error.required' }),
    to: z.date().optional().nullable(),
  }),
});

type DateRangeFormData = z.infer<typeof dateRangeSchema>;

const singleDateSchema = z.object({
  date: z.date({ required_error: 'common:error.required' }),
});

type SingleDateFormData = z.infer<typeof singleDateSchema>;

const TODAY_START = () => new Date(new Date().setHours(0, 0, 0, 0));

/**
 * Generic footer component for feature details pages.
 *
 * Provides mode-specific functionality:
 * - Create mode: Submit button only
 * - Edit mode: Date picker + submit button (range or single based on useSingleDatePicker)
 * - EditChange mode: Date range picker + submit + cancel buttons
 * - Read mode: Hidden (no footer)
 */
export function FeatureDetailsFooter({
  mode,
  isLoading,
  hasFormChanges,
  isFormValid = true,
  onSubmit,
  onCancel,
  selectedDate: initialDate,
  selectedEndDate: initialEndDate,
  t,
  isVisible = true,
  labels = {},
  showDatePicker = true,
  useSingleDatePicker = false,
  disablePastDates = false,
  isDatePickerDisabled = false,
  minDate,
}: FeatureFooterProps) {
  const dateRangeForm = useForm<DateRangeFormData>({
    resolver: zodResolver(dateRangeSchema),
  });

  const singleDateForm = useForm<SingleDateFormData>({
    resolver: zodResolver(singleDateSchema),
  });

  // Only watch the form that's actually in use to avoid unnecessary re-renders
  const selectedDateRange = useSingleDatePicker ? undefined : dateRangeForm.watch('dateRange');
  const selectedSingleDate = useSingleDatePicker ? singleDateForm.watch('date') : undefined;

  // Ref to track if user has manually set a date — avoids re-render cycles
  const userHasSetDateRef = useRef(false);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const defaultLabels = {
    saving: labels.saving || t('common:loading'),
    create: labels.create || t('common:create'),
    saveChanges: labels.saveChanges || t('common:saveChanges'),
    updateRecord: labels.updateRecord || t('common:update'),
    cancel: labels.cancel || t('common:cancel'),
  };

  // Track when the user manually sets a date so auto-population doesn't override it
  useEffect(() => {
    const hasDate = useSingleDatePicker ? !!selectedSingleDate : !!selectedDateRange?.from;
    if (hasDate) {
      userHasSetDateRef.current = true;
    } else if (!hasFormChanges) {
      userHasSetDateRef.current = false;
    }
  }, [selectedDateRange, selectedSingleDate, hasFormChanges, useSingleDatePicker]);

  // Auto-populate date when form changes are detected in Edit mode
  useEffect(() => {
    if (mode !== FeatureMode.Edit) return;

    if (useSingleDatePicker) {
      if (singleDateForm.getValues('date')) return;
      if (hasFormChanges && !isDatePickerDisabled && !userHasSetDateRef.current) {
        // Default to the earliest selectable date: today, unless a future minDate
        // is set (e.g. changes that can only be scheduled from tomorrow onward).
        const defaultDate =
          minDate && isBefore(TODAY_START(), startOfDay(minDate)) ? minDate : new Date();
        singleDateForm.setValue('date', defaultDate, { shouldDirty: false, shouldValidate: true });
      } else if (!hasFormChanges || isDatePickerDisabled) {
        userHasSetDateRef.current = false;
      }
    } else {
      if (dateRangeForm.getValues('dateRange')?.from) return;
      if (!hasFormChanges || isDatePickerDisabled) {
        dateRangeForm.reset({ dateRange: { from: undefined as any, to: undefined } });
        userHasSetDateRef.current = false;
      } else if (!userHasSetDateRef.current) {
        dateRangeForm.setValue(
          'dateRange',
          { from: new Date(), to: null },
          { shouldDirty: false, shouldValidate: true }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hasFormChanges, isDatePickerDisabled, useSingleDatePicker]);

  // Pre-populate date range from initialDate/initialEndDate in EditChange mode
  useEffect(() => {
    if (mode === FeatureMode.EditChange && initialDate) {
      dateRangeForm.setValue(
        'dateRange',
        {
          from: new Date(initialDate),
          to: initialEndDate ? new Date(initialEndDate) : null,
        },
        { shouldDirty: false, shouldValidate: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialDate, initialEndDate]);

  const isBeforeToday = useCallback((date: Date) => date < TODAY_START(), []);

  // Combined matcher for non-selectable dates: disable past dates (when
  // requested) and any date before the provided minimum (e.g. project start
  // date for versioned changes). Undefined when neither restriction applies, so
  // pickers without date limits are unaffected.
  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      if (disablePastDates && isBeforeToday(date)) return true;
      if (minDate && isBefore(startOfDay(date), startOfDay(minDate))) return true;
      return false;
    },
    [disablePastDates, isBeforeToday, minDate]
  );
  const hasDateRestriction = disablePastDates || !!minDate;
  const dateDisabledMatcher = hasDateRestriction ? isDateDisabled : undefined;

  if (!isVisible || mode === FeatureMode.Read) {
    return null;
  }

  // In EditChange mode the effective date range is itself an editable field:
  // changing only the range (without touching any tab field) must enable the
  // save button (OPS-1575). Detect a day-level divergence from the initial range.
  const isEditChangeMode = mode === FeatureMode.EditChange;

  const isSameNullableDay = (a: Date | undefined | null, b: Date | undefined | null): boolean => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return isSameDay(a, b);
  };

  const isDateRangeDirty =
    isEditChangeMode &&
    showDatePicker &&
    !isDatePickerDisabled &&
    (!isSameNullableDay(selectedDateRange?.from, initialDate ? new Date(initialDate) : undefined) ||
      !isSameNullableDay(selectedDateRange?.to, initialEndDate ? new Date(initialEndDate) : null));

  // Treat a date-range change as a form change in EditChange mode so the save
  // button enables even when no tab field is dirty.
  const effectiveHasChanges = hasFormChanges || isDateRangeDirty;

  const isFormDisabled = !effectiveHasChanges || !isFormValid;

  if (mode === FeatureMode.Create) {
    return (
      <div className="z-[5] shrink-0 border-t border-border bg-surface px-4 py-3 shadow-[0_-1px_4px_0_hsl(var(--border))]">
        <div className="flex items-center justify-end">
          <ButtonLoadable
            onClick={() => onSubmit()}
            isLoading={isLoading}
            disabled={isFormDisabled || isLoading}
          >
            {isLoading ? defaultLabels.saving : defaultLabels.create}
          </ButtonLoadable>
        </div>
      </div>
    );
  }

  const activeDateForm = useSingleDatePicker ? singleDateForm : dateRangeForm;
  const isDatePickerInvalid =
    showDatePicker && !isDatePickerDisabled && !activeDateForm.formState.isValid;
  const isSubmitDisabled = isFormDisabled || isLoading || isDatePickerInvalid || isDatePickerOpen;
  // EditChange: the effective date range stays editable regardless of form
  // dirtiness (OPS-1575) — only honor the explicit isDatePickerDisabled flag.
  // Edit mode keeps the original "enabled once the form is dirty" behavior.
  const shouldDisableDatePicker = isEditChangeMode
    ? isDatePickerDisabled
    : isFormDisabled || isDatePickerDisabled;
  const showCancelButton = mode === FeatureMode.EditChange;
  const submitButtonText =
    mode === FeatureMode.EditChange ? defaultLabels.updateRecord : defaultLabels.saveChanges;

  const handleSubmit = async () => {
    if (showDatePicker && !isDatePickerDisabled) {
      if (useSingleDatePicker) {
        const result = await singleDateForm.trigger('date');
        if (result) {
          onSubmit({ from: singleDateForm.getValues('date'), to: null });
        }
      } else {
        const result = await dateRangeForm.trigger('dateRange');
        if (result) {
          const dateRange = dateRangeForm.getValues('dateRange');
          onSubmit({ from: dateRange.from, to: dateRange.to });
        }
      }
    } else {
      // No date picker or disabled (only non-versioned changes), submit without date range
      onSubmit();
    }
  };

  const datePickerContent = showDatePicker && (
    <>
      <span className="hidden whitespace-nowrap text-sm font-medium text-foreground sm:inline">
        {labels.dateOfChange ?? t('common:dateRangeLabels.effectiveDates')}
      </span>
      {useSingleDatePicker ? (
        <DatePicker
          name="date"
          control={singleDateForm.control}
          hideLabel
          dateFormat={STANDARD_DATE_FORMAT_DAYJS}
          inputDisabled={shouldDisableDatePicker}
          placeholder={labels.selectChangeDate}
          disabled={dateDisabledMatcher}
        />
      ) : (
        <DateRangePicker
          name="dateRange"
          control={dateRangeForm.control}
          label=""
          dateFormat={STANDARD_DATE_FORMAT_DAYJS}
          showNoEndDateOption={true}
          disabled={shouldDisableDatePicker}
          disabledDates={dateDisabledMatcher}
          onOpenChange={setIsDatePickerOpen}
        />
      )}
    </>
  );

  const footerBody = (
    <div className="z-[5] shrink-0 border-t border-border bg-surface px-4 py-3 shadow-[0_-1px_4px_0_hsl(var(--border))]">
      <div
        className={
          showCancelButton
            ? 'flex items-center justify-between'
            : 'flex items-center justify-end gap-4'
        }
      >
        {showCancelButton && (
          <Button variant="outline" onClick={onCancel} type="button">
            {defaultLabels.cancel}
          </Button>
        )}
        <div className="flex items-center gap-4">
          {datePickerContent}
          <ButtonLoadable onClick={handleSubmit} isLoading={isLoading} disabled={isSubmitDisabled}>
            {isLoading ? defaultLabels.saving : submitButtonText}
          </ButtonLoadable>
        </div>
      </div>
    </div>
  );

  return useSingleDatePicker ? (
    <Form {...singleDateForm}>{footerBody}</Form>
  ) : (
    <Form {...dateRangeForm}>{footerBody}</Form>
  );
}
